import { useState, useEffect } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { paymentsAPI } from '@/lib/api';
import { formatCurrency, formatDate, getStatusColor } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MagnifyingGlass, Export, X, Check, Money } from '@phosphor-icons/react';
import PaymentDetailDialog from '@/components/PaymentDetailDialog';
import CustomerDrillDownDialog from '@/components/CustomerDrillDownDialog';
import RecordAdvanceDialog from '@/components/RecordAdvanceDialog';
import { banksAPI } from '@/lib/api';
import { toast } from 'sonner';

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [drill, setDrill] = useState(null);
  const [banks, setBanks] = useState([]);
  const [showAdvance, setShowAdvance] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({
    reconciled: 'all',
    search: '',
    start_date: '',
    end_date: '',
    payment_mode: '',
    deposit_to: '',
    min_amount: '',
    max_amount: '',
  });

  const loadPayments = async () => {
    setLoading(true);
    try {
      const params = {
        limit: 50,
        skip: (page - 1) * 50,
      };
      if (filters.reconciled !== 'all') params.reconciled = filters.reconciled === 'true';
      if (filters.search) params.search = filters.search;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.payment_mode) params.payment_mode = filters.payment_mode;
      if (filters.deposit_to) params.deposit_to = filters.deposit_to;
      if (filters.min_amount) params.min_amount = parseFloat(filters.min_amount);
      if (filters.max_amount) params.max_amount = parseFloat(filters.max_amount);
      
      const response = await paymentsAPI.list(params);
      setPayments(response.data.payments || []);
      setTotal(response.data.total || 0);
      setTotalAmount(response.data.total_amount || 0);
    } catch (error) {
      console.error('Failed to load payments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    banksAPI.list().then(({ data }) => setBanks(data.banks || [])).catch(() => {});
  }, []);

  // Debounced reload whenever any filter or page changes (fixes #10 search)
  useEffect(() => {
    const t = setTimeout(loadPayments, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters.reconciled, filters.search, filters.start_date, filters.end_date, filters.payment_mode, filters.deposit_to, filters.min_amount, filters.max_amount]);

  const hasActiveFilters = filters.start_date || filters.end_date || filters.min_amount || filters.max_amount || filters.payment_mode || filters.deposit_to || filters.search;

  const clearFilters = () => {
    setFilters({ reconciled: 'all', search: '', start_date: '', end_date: '', payment_mode: '', deposit_to: '', min_amount: '', max_amount: '' });
    setPage(1);
  };

  const buildFilterParams = () => {
    const params = {};
    if (filters.reconciled !== 'all') params.reconciled = filters.reconciled === 'true';
    if (filters.search) params.search = filters.search;
    if (filters.start_date) params.start_date = filters.start_date;
    if (filters.end_date) params.end_date = filters.end_date;
    if (filters.payment_mode) params.payment_mode = filters.payment_mode;
    if (filters.deposit_to) params.deposit_to = filters.deposit_to;
    if (filters.min_amount) params.min_amount = parseFloat(filters.min_amount);
    if (filters.max_amount) params.max_amount = parseFloat(filters.max_amount);
    return params;
  };

  const csvCell = (v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await paymentsAPI.list({ ...buildFilterParams(), limit: 100000, skip: 0 });
      const rows = data.payments || [];
      if (rows.length === 0) { toast.error('No payments match the current filters'); return; }
      const headers = ['Payment #', 'Customer', 'Date', 'Amount', 'Mode', 'Reference', 'Deposit Account', 'Invoice #', 'Reconciled'];
      const lines = [headers.join(',')];
      rows.forEach((p) => {
        lines.push([
          p.payment_number || p.payment_number_suffix || p.id,
          p.customer_name,
          p.payment_date,
          p.amount,
          (p.payment_mode || p.mode || '').replace('_', ' '),
          p.reference_number || p.reference || p.cf_card_cheque_number || '',
          p.account_name || p.deposit_to || '',
          p.invoice_number || '',
          p.reconciled ? 'Yes' : 'No',
        ].map(csvCell).join(','));
      });
      const csv = '\ufeff' + lines.join('\n');
      const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `payments_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} payment${rows.length === 1 ? '' : 's'}`);
    } catch (error) {
      console.error('Failed to export payments:', error);
      toast.error('Failed to export payments');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="payments-page">
      <PageHeader 
        title="Payments" 
        subtitle={`${total} total payments`}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setShowAdvance(true)} data-testid="record-advance-btn">
              <Money size={16} className="mr-2" />
              Record Advance
            </Button>
            <Button className="bg-[#0F4C81] hover:bg-[#0B3C68]" onClick={handleExport} disabled={exporting} data-testid="export-payments-btn">
              <Export size={16} className="mr-2" />
              {exporting ? 'Exporting…' : 'Export'}
            </Button>
          </div>
        }
      />
      
      <div className="page-content">
        {/* Totals */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
            <p className="text-xs text-[#64748B] mb-1">Total Payments</p>
            <p className="text-lg font-semibold font-['Plus_Jakarta_Sans']">{total}</p>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
            <p className="text-xs text-[#64748B] mb-1">Total Amount</p>
            <p className="text-lg font-semibold font-['Plus_Jakarta_Sans'] text-green-600">{formatCurrency(totalAmount)}</p>
          </div>
        </div>

        {/* Filters - always visible */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 mb-4" data-testid="payments-date-filters">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
              <Input
                placeholder="Search payments..."
                value={filters.search}
                onChange={(e) => { setFilters(prev => ({ ...prev, search: e.target.value })); setPage(1); }}
                className="pl-10"
                data-testid="search-payments"
              />
            </div>
            <Select
              value={filters.reconciled}
              onValueChange={(value) => { setFilters(prev => ({ ...prev, reconciled: value })); setPage(1); }}
            >
              <SelectTrigger className="w-40" data-testid="filter-reconciled">
                <SelectValue placeholder="All Payments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payments</SelectItem>
                <SelectItem value="true">Reconciled</SelectItem>
                <SelectItem value="false">Unreconciled</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.payment_mode || 'all'}
              onValueChange={(value) => { setFilters(prev => ({ ...prev, payment_mode: value === 'all' ? '' : value })); setPage(1); }}
            >
              <SelectTrigger className="w-36" data-testid="filter-payment-mode">
                <SelectValue placeholder="All Modes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modes</SelectItem>
                {['Cash', 'UPI', 'Bank Transfer', 'Card', 'Credit Card', 'Cheque', 'Razorpay', 'PayTM', 'Net Banking'].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select
              value={filters.deposit_to || 'all'}
              onValueChange={(value) => { setFilters(prev => ({ ...prev, deposit_to: value === 'all' ? '' : value })); setPage(1); }}
            >
              <SelectTrigger className="w-44" data-testid="filter-deposit-to">
                <SelectValue placeholder="All Deposit Accounts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Deposit Accounts</SelectItem>
                {banks.map(b => <SelectItem key={b.id} value={b.account_name}>{b.account_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="h-6 w-px bg-[#E2E8F0]" />
            <div className="flex items-center gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wide text-[#64748B] block mb-0.5">From</label>
                <Input type="date" value={filters.start_date} onChange={(e) => { setFilters(prev => ({ ...prev, start_date: e.target.value })); setPage(1); }} className="w-40 h-9" data-testid="filter-start-date" />
              </div>
              <span className="text-[#64748B] mt-4">—</span>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-[#64748B] block mb-0.5">To</label>
                <Input type="date" value={filters.end_date} onChange={(e) => { setFilters(prev => ({ ...prev, end_date: e.target.value })); setPage(1); }} className="w-40 h-9" data-testid="filter-end-date" />
              </div>
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}><X size={14} className="mr-1" /> Clear</Button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Payment #</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Mode</th>
                <th>Reference</th>
                <th>Reconciled</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(10)].map((_, i) => (
                  <tr key={i}>
                    {[...Array(7)].map((_, j) => (
                      <td key={j}><div className="skeleton h-4 w-20 rounded"></div></td>
                    ))}
                  </tr>
                ))
              ) : payments.length > 0 ? (
                payments.map((payment) => (
                  <tr key={payment.id} data-testid={`payment-row-${payment.payment_number}`} className="cursor-pointer hover:bg-[#F8FAFC]" onClick={() => setSelectedPayment(payment)}>
                    <td className="font-medium text-[#0F4C81]">{payment.payment_number}</td>
                    <td onClick={e => e.stopPropagation()}>
                      {payment.customer_name ? (
                        <button onClick={() => setDrill({ id: payment.customer_id, name: payment.customer_name })} className="text-[#0F4C81] underline decoration-dotted underline-offset-2 hover:text-[#0F4C81] text-left" data-testid={`payment-customer-${payment.payment_number}`}>{payment.customer_name}</button>
                      ) : '-'}
                    </td>
                    <td>{formatDate(payment.payment_date)}</td>
                    <td className="font-medium text-green-600">{formatCurrency(payment.amount)}</td>
                    <td className="capitalize">{(payment.payment_mode || payment.mode || '')?.replace('_', ' ')}</td>
                    <td className="text-[#64748B]">
                      {payment.reference_number || payment.reference || payment.cf_card_cheque_number || '-'}
                      {(payment.account_name || payment.deposit_to) && (
                        <span className="block text-[10px] text-emerald-700">→ {payment.account_name || payment.deposit_to}</span>
                      )}
                    </td>
                    <td>
                      {payment.reconciled ? (
                        <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                          <Check size={14} weight="bold" />
                          Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#DC2626] text-sm">
                          <X size={14} weight="bold" />
                          No
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-[#64748B]">
                    No payments found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > 50 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-[#64748B]">
              Showing {(page - 1) * 50 + 1} to {Math.min(page * 50, total)} of {total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                disabled={page * 50 >= total}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      <PaymentDetailDialog payment={selectedPayment} open={!!selectedPayment} onClose={() => setSelectedPayment(null)} admin banks={banks} onChanged={loadPayments} />
      <CustomerDrillDownDialog open={!!drill} onOpenChange={o => { if (!o) setDrill(null); }} customerId={drill?.id || ''} customerName={drill?.name || ''} />
      <RecordAdvanceDialog open={showAdvance} onOpenChange={setShowAdvance} onSaved={loadPayments} />
    </div>
  );
}
